import React, { MouseEvent, useState } from "react";
import { flushSync } from "react-dom";
import { FiChevronDown } from "react-icons/fi";
import { PolicySection, PolicySectionId } from "./policyContent";

interface PolicyTocProps {
	sections: PolicySection[];
	activeId: PolicySectionId | null;
	onNavigate: (id: PolicySectionId) => void;
}

const TOC_LIST_ID = "pp-toc-list";

/**
 * "On this page" navigation. A sticky sidebar from 1024px up; a collapsible
 * disclosure below that (the collapsed state only applies on small screens via CSS).
 */
const PolicyToc = ({ sections, activeId, onNavigate }: PolicyTocProps) => {
	const [open, setOpen] = useState(false);

	const handleClick = (event: MouseEvent<HTMLAnchorElement>, id: PolicySectionId) => {
		// Let modified clicks (new tab, etc.) behave normally.
		if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
		event.preventDefault();
		// Collapse synchronously so the layout above the target is final before scrolling.
		flushSync(() => setOpen(false));
		onNavigate(id);
	};

	return (
		<nav className={`pp-toc${open ? " is-open" : ""}`} aria-label="On this page">
			<button
				type="button"
				className="pp-toc-toggle"
				aria-expanded={open}
				aria-controls={TOC_LIST_ID}
				onClick={() => setOpen((value: boolean) => !value)}
			>
				<span>On this page</span>
				<FiChevronDown className="pp-toc-chevron" aria-hidden="true" />
			</button>
			<p className="pp-toc-heading" aria-hidden="true">
				On this page
			</p>
			<ol className="pp-toc-list" id={TOC_LIST_ID}>
				{sections.map((section: PolicySection) => {
					const isActive = section.id === activeId;
					return (
						<li key={section.id}>
							<a
								href={`#${section.id}`}
								className={`pp-toc-link${isActive ? " is-active" : ""}`}
								aria-current={isActive ? "location" : undefined}
								onClick={(event: MouseEvent<HTMLAnchorElement>) => handleClick(event, section.id)}
							>
								<span className="pp-toc-number">{section.number}.</span>
								<span>{section.title}</span>
							</a>
						</li>
					);
				})}
			</ol>
		</nav>
	);
};

export default PolicyToc;
